import { Box, styled, TextField, Typography } from '@chronosphereio/chrono-ui';
import { isTimeRangeInputValueAbsolute, type TimeFormat, type TimeRangeInputValue } from '@chronosphereio/core';
import { type ChangeEvent, type ReactNode, useState } from 'react';
import { absoluteTimeRangeDatesHaveCorrectOrder, parseTimeRange } from '../utils';
import { type ErrorOptionKey, getErrorOptionLabels } from '../utils/options';

interface TimeRangeSearchFieldProps {
  searchValue: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onSelect: (value: TimeRangeInputValue) => void;
  timeZone: string;
  timeFormat: TimeFormat;
  copyButton: ReactNode;
  lastLoadedMessage?: string;
  enableNewLogoStubYear?: boolean;
  /** ID of the listbox for combobox ARIA linkage */
  listboxId?: string;
  /** Ref to the listbox for arrow key focus transfer */
  listRef?: React.RefObject<HTMLUListElement | null>;
  /** Ref to the input for focus management */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /** Whether there are filtered options available to navigate to */
  hasFilteredOptions?: boolean;
  /** External error message to display (e.g. from paste validation) */
  errorMessage?: string;
}

export function TimeRangeSearchField({
  searchValue,
  onChange,
  onSelect,
  timeZone,
  timeFormat,
  copyButton,
  lastLoadedMessage,
  enableNewLogoStubYear,
  listboxId,
  listRef,
  inputRef,
  hasFilteredOptions = true,
  errorMessage,
}: TimeRangeSearchFieldProps) {
  const [inputError, setInputError] = useState<false | ErrorOptionKey>(false);
  const errorOptionLabels = getErrorOptionLabels(enableNewLogoStubYear);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle combobox arrow key navigation
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && listRef?.current && hasFilteredOptions) {
      e.preventDefault();
      listRef.current.focus();

      // If ArrowUp, dispatch another ArrowUp to wrap to the bottom of the list
      // This matches expected combobox behavior without having to pass around
      // more refs between components
      if (e.key === 'ArrowUp') {
        const arrowUpEvent = new KeyboardEvent('keydown', {
          key: 'ArrowUp',
          bubbles: true,
          cancelable: true,
        });
        listRef.current.dispatchEvent(arrowUpEvent);
      }
      return;
    }

    // Handle Enter to apply value
    if (e.key !== 'Enter') return;
    e.preventDefault();

    if (!searchValue.trim()) return;

    const parsed = parseTimeRange(searchValue, timeZone);
    if (!parsed) {
      setInputError(timeFormat === '24h' ? 'UNSUPPORTED_24H' : 'UNSUPPORTED_12H');
      return;
    }
    if (isTimeRangeInputValueAbsolute(parsed) && !absoluteTimeRangeDatesHaveCorrectOrder(parsed)) {
      setInputError('INVALID_ORDER');
      return;
    }
    setInputError(false);
    onSelect(parsed);
  };

  return (
    <SearchFieldSection display="flex" flexDirection="column" gap={1}>
      <Box display="flex" flexDirection="row" gap={1} alignItems="flex-start">
        <StyledTextField
          // this is in line with the a11y spec as the input is the first focusable element in the panel
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          inputRef={inputRef}
          onFocus={(e) => e.target.select()}
          placeholder="Enter time range"
          value={searchValue}
          error={!!inputError || !!errorMessage}
          helperText={inputError ? errorOptionLabels[inputError] : errorMessage}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            onChange(e);
            setInputError(false);
          }}
          onKeyDown={handleKeyDown}
          inputProps={{
            ...(listboxId && {
              'aria-controls': listboxId,
              'aria-expanded': true,
              'aria-haspopup': 'listbox',
              'aria-autocomplete': 'list',
            }),
          }}
        />
        {copyButton}
      </Box>
      {lastLoadedMessage && (
        <Typography variant="body2" color="text.placeholder">
          {lastLoadedMessage}
        </Typography>
      )}
    </SearchFieldSection>
  );
}

const SearchFieldSection = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledTextField = styled(TextField)({
  width: '100%',
});
